import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { resolvePreferredLeagues } from '../utils/leaguePreference';
import { formatRoundDisplay } from '../utils/formatRoundDisplay';
import LeagueCircleSelector from '../components/LeagueCircleSelector';
import LoadingScreen from '../components/LoadingScreen';
import { LeagueCirclePlaceholders } from '../components/LoadingPlaceholders';
import {
  getAugmentDisplayName,
  getAugmentShortDesc,
  getAugmentStyleSvgPath,
  isVoidedGame
} from '../utils/augments';
import {
  BRAZIL_STYLE_ID,
  CYBERPUNK_STYLE_ID,
  FIRE_STYLE_ID,
  GOLD_STYLE_ID,
  TROPICAL_STYLE_ID
} from 'arena-shared';
import ClubCrest from '../components/ClubCrest';
import TeamName from '../components/TeamName';
import MatchLeagueTablePopover from '../components/MatchLeagueTablePopover';
import LiveMatchBadge from '../components/LiveMatchBadge';
import MatchEventsSection from '../components/MatchEventsSection';
import GameStatsRecap from '../components/GameStatsRecap';
import {
  isGameLive,
  needsHomeDisplayClock,
  pickFeaturedGamesForHome,
  sortGamesForHomeDisplay
} from '../utils/homeGameDisplayOrder';
import { getGameStatusLabel } from '../utils/gameStatusLabel';
import { useLiveGameSync } from '../hooks/useLiveGameSync';
import { mergeLiveGameIntoGamesList } from '../utils/mergeLiveGame';
import {
  toastFromStandingsProcessingEvent,
  toastFromStandingsUpdatedEvent
} from '../utils/standingsProcessingToast';
import {
  applyLiveEventToHomeCache,
  collectGamesFromHomeCache,
  homeByLeagueStore,
  resolveSessionSelectedLeague,
  selectedLeagueCodeStore
} from '../utils/leaguePageCache';
import {
  findUnseenFinishedGames,
  getGamesSeenAt,
  maxGameResultTimestamp,
  setGamesSeenAt
} from '../utils/gamesSeenAt';
import './Betting.css';
import './Home.css';

/** Curated play × style pairs for the logged-out hero — newest / flashiest styles first. */
const HERO_PLAY_SHOWCASE = [
  { code: 'gd', styleId: BRAZIL_STYLE_ID },
  { code: 'sa', styleId: CYBERPUNK_STYLE_ID },
  { code: 'bs', styleId: FIRE_STYLE_ID },
  { code: 'eif', styleId: GOLD_STYLE_ID },
  { code: 's', styleId: TROPICAL_STYLE_ID }
].map((item) => ({
  key: `${item.code}#${item.styleId}`,
  code: item.code,
  label: getAugmentDisplayName(item.code),
  shortDesc: getAugmentShortDesc(item.code),
  svg: getAugmentStyleSvgPath(item.code, item.styleId)
}));

const Home = () => {
  const { user, loading: authLoading } = useAuth();
  const preferredLeagues = useMemo(
    () => resolvePreferredLeagues(user?.lp, user?.ld),
    [user?.lp, user?.ld]
  );
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [currentWeek, setCurrentWeek] = useState(null);
  const [loadedLeagueCode, setLoadedLeagueCode] = useState(null);
  const [games, setGames] = useState([]);
  const [previousGames, setPreviousGames] = useState([]);
  const [resultsCta, setResultsCta] = useState(null);
  const [liveResultToast, setLiveResultToast] = useState(null);
  /** leagueCode → { currentWeek, games, previousGames } — seeded from session store so SPA navigations keep cache */
  const [homeByLeague, setHomeByLeagueState] = useState(() => ({ ...homeByLeagueStore.get() }));
  const homeByLeagueRef = useRef(homeByLeagueStore.get());
  const selectedLeagueRef = useRef(null);
  const setHomeByLeague = useCallback((updater) => {
    setHomeByLeagueState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      homeByLeagueStore.set(next);
      homeByLeagueRef.current = next;
      return next;
    });
  }, []);
  homeByLeagueRef.current = homeByLeague;
  selectedLeagueRef.current = selectedLeague;
  const [loading, setLoading] = useState(true);
  const [leagueLoading, setLeagueLoading] = useState(false);
  const [error, setError] = useState('');
  const [displayNow, setDisplayNow] = useState(() => Date.now());

  const trackHomeEvent = (eventName, payload = {}) => {
    const eventPayload = {
      event: eventName,
      page: 'home',
      authState: user ? 'authenticated' : 'anonymous',
      leagueCode: selectedLeague?.code || null,
      timestamp: new Date().toISOString(),
      ...payload
    };
    if (window?.dataLayer && Array.isArray(window.dataLayer)) {
      window.dataLayer.push(eventPayload);
    }
    if (window?.gtag) {
      window.gtag('event', eventName, eventPayload);
    }
  };

  useEffect(() => {
    // Wait for auth restore so default PL is not locked in before the user's preference loads.
    if (authLoading) return;
    if (preferredLeagues.length === 0) return;
    setSelectedLeague((current) => resolveSessionSelectedLeague(preferredLeagues, current));
  }, [preferredLeagues, authLoading]);

  useEffect(() => {
    if (selectedLeague) {
      loadHomeForSelectedLeague();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- cache on revisit; only when league changes
  }, [selectedLeague?.code]);

  useEffect(() => {
    if (!selectedLeague?.code) return;
    // Avoid caching the previous league's games under the newly selected code.
    if (loadedLeagueCode !== selectedLeague.code) return;
    setHomeByLeague((prev) => {
      const entry = {
        currentWeek,
        games,
        previousGames
      };
      const prevEntry = prev[selectedLeague.code];
      if (
        prevEntry &&
        prevEntry.currentWeek === currentWeek &&
        prevEntry.games === games &&
        prevEntry.previousGames === previousGames
      ) {
        return prev;
      }
      return { ...prev, [selectedLeague.code]: entry };
    });
  }, [selectedLeague?.code, currentWeek, games, previousGames, loadedLeagueCode, setHomeByLeague]);

  useEffect(() => {
    trackHomeEvent('home_viewed');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, selectedLeague?.code]);

  const sortedGames = useMemo(
    () => sortGamesForHomeDisplay(games, displayNow),
    [games, displayNow]
  );

  const featuredGames = useMemo(
    () => pickFeaturedGamesForHome(games, 3, displayNow),
    [games, displayNow]
  );

  useEffect(() => {
    if (!needsHomeDisplayClock(games, displayNow)) return undefined;
    const timerId = window.setInterval(() => setDisplayNow(Date.now()), 60_000);
    return () => window.clearInterval(timerId);
  }, [games, displayNow]);

  const handleLeagueSelect = (league) => {
    if (selectedLeague?.code === league.code) return;
    if (loadedLeagueCode !== null && !homeByLeagueRef.current[league.code]) {
      setLeagueLoading(true);
    }
    selectedLeagueCodeStore.set(league.code);
    setSelectedLeague(league);
  };

  const applyHomePayload = (leagueCode, round, gamesList, previousGamesList = []) => {
    setHomeByLeague((prev) => ({
      ...prev,
      [leagueCode]: {
        currentWeek: round,
        games: gamesList,
        previousGames: previousGamesList
      }
    }));
    if (selectedLeagueRef.current?.code === leagueCode) {
      setCurrentWeek(round);
      setLoadedLeagueCode(leagueCode);
      setGames(gamesList);
      setPreviousGames(previousGamesList);
    }
  };

  const dismissResultsCta = useCallback((candidateGames) => {
    const username = user?.username;
    if (!username) {
      setResultsCta(null);
      return;
    }
    const advanceTo = Math.max(Date.now(), maxGameResultTimestamp(candidateGames || []));
    setGamesSeenAt(username, advanceTo);
    setResultsCta(null);
  }, [user?.username]);

  const evaluateResultsCta = useCallback((leagueCode, currentList, previousList) => {
    const username = user?.username;
    if (!username || !leagueCode) {
      setResultsCta(null);
      return;
    }
    const lastSeen = getGamesSeenAt(username);
    if (lastSeen == null) {
      setGamesSeenAt(username, Date.now());
      setResultsCta(null);
      return;
    }
    const unseen = findUnseenFinishedGames(
      [...(previousList || []), ...(currentList || [])],
      lastSeen
    );
    if (unseen.length === 0) {
      setResultsCta(null);
      return;
    }
    setResultsCta({
      leagueCode,
      count: unseen.length,
      candidates: unseen
    });
  }, [user?.username]);

  const fetchCurrentWeek = async ({ force = false } = {}) => {
    const league = selectedLeagueRef.current;
    if (!league) return;

    const leagueCode = league.code;
    const isInitialLoad = loadedLeagueCode === null;

    if (!force) {
      const cached = homeByLeagueRef.current[leagueCode];
      if (cached) {
        setCurrentWeek(cached.currentWeek);
        setGames(cached.games || []);
        setPreviousGames(cached.previousGames || []);
        setLoadedLeagueCode(leagueCode);
        setLoading(false);
        setLeagueLoading(false);
        setError('');
        evaluateResultsCta(leagueCode, cached.games || [], cached.previousGames || []);
        return;
      }
    }

    try {
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setLeagueLoading(true);
      }
      setError('');
      const response = await api.get(`/rounds/${leagueCode}/current`);
      const round = response.data;
      const previousList = Array.isArray(round.previousGames) ? round.previousGames : [];
      applyHomePayload(leagueCode, round, round.games || [], previousList);
      evaluateResultsCta(leagueCode, round.games || [], previousList);
    } catch (err) {
      if (err.response?.status === 404) {
        applyHomePayload(leagueCode, null, [], []);
        setResultsCta(null);
        if (selectedLeagueRef.current?.code === leagueCode) {
          setError('');
        }
      } else {
        if (selectedLeagueRef.current?.code === leagueCode) {
          setError('Failed to load current round');
        }
        console.error(err);
      }
    } finally {
      if (selectedLeagueRef.current?.code === leagueCode) {
        setLoading(false);
        setLeagueLoading(false);
      }
    }
  };

  const loadHomeForSelectedLeague = () => {
    const league = selectedLeagueRef.current;
    if (!league) return;
    const cached = homeByLeagueRef.current[league.code];
    if (cached) {
      setCurrentWeek(cached.currentWeek);
      setGames(cached.games || []);
      setPreviousGames(cached.previousGames || []);
      setLoadedLeagueCode(league.code);
      setLoading(false);
      setLeagueLoading(false);
      setError('');
      evaluateResultsCta(league.code, cached.games || [], cached.previousGames || []);
      return;
    }
    fetchCurrentWeek({ force: true });
  };

  const liveSyncGames = useMemo(
    () => collectGamesFromHomeCache(homeByLeague, games),
    [homeByLeague, games]
  );

  const handleLiveGameEvent = useCallback((event) => {
    const processingToast = toastFromStandingsProcessingEvent(event);
    if (processingToast) {
      setLiveResultToast(processingToast);
      return;
    }

    const updatedToast = toastFromStandingsUpdatedEvent(event);
    if (updatedToast) {
      setLiveResultToast((prev) => {
        if (!prev || prev.id !== updatedToast.id) return prev;
        return updatedToast;
      });
      return;
    }

    const snapshot = event?.game;
    const gameId = event?.gameId || snapshot?.gameId;
    if (!gameId || !snapshot) return;
    setGames((prev) => mergeLiveGameIntoGamesList(prev, gameId, snapshot));
    setHomeByLeague((prev) => applyLiveEventToHomeCache(prev, gameId, snapshot));
    setCurrentWeek((prev) => {
      if (!prev?.games) return prev;
      const nextGames = mergeLiveGameIntoGamesList(prev.games, gameId, snapshot);
      if (nextGames === prev.games) return prev;
      return { ...prev, games: nextGames };
    });
    if (event?.type === 'GAME_ENDED' && snapshot.status === 'finished') {
      const home = snapshot.homeTeamTla || snapshot.homeTeam || 'Home';
      const away = snapshot.awayTeamTla || snapshot.awayTeam || 'Away';
      const hs = snapshot.homeScore ?? '?';
      const as = snapshot.awayScore ?? '?';
      setLiveResultToast({
        id: gameId,
        message: `Full time: ${home} ${hs}–${as} ${away}. Check standings.`
      });
    }
  }, [setHomeByLeague]);

  useEffect(() => {
    if (!liveResultToast) return undefined;
    const timerId = window.setTimeout(() => setLiveResultToast(null), 8000);
    return () => window.clearTimeout(timerId);
  }, [liveResultToast]);

  useLiveGameSync({
    games: liveSyncGames,
    onLiveEvent: handleLiveGameEvent,
    onVisibilityResume: () => fetchCurrentWeek({ force: true }),
    enabled: Boolean(selectedLeague),
    onSubscribed: () => fetchCurrentWeek({ force: true })
  });

  const getUrgencyMessage = () => {
    if (!currentWeek) return 'New rounds open regularly. Join now so you do not miss the next one.';
    const now = new Date();
    const startDate = currentWeek.roundStartDate ? new Date(currentWeek.roundStartDate) : null;
    const endDate = currentWeek.roundEndDate ? new Date(currentWeek.roundEndDate) : null;

    if (currentWeek.status === 'upcoming' && startDate) {
      return 'Round is about to begin - lock in your picks, activate your plays, and attack the matches below.';
    }

    if (currentWeek.status === 'active' && startDate) {
      return 'The round is on - follow the action below and get your next picks and plays ready.';
    }

    return 'New rounds open regularly. Join now so you do not miss the next one.';
  };

  const formatRoundDate = (dateValue) => {
    if (!dateValue) return '';
    return new Date(dateValue).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  };

  const formatRoundDateRange = (startValue, endValue) => {
    if (!startValue && !endValue) return '';
    if (!startValue) return formatRoundDate(endValue);
    if (!endValue) return formatRoundDate(startValue);
    const start = new Date(startValue);
    const end = new Date(endValue);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '';
    if (
      start.getFullYear() === end.getFullYear() &&
      start.getMonth() === end.getMonth() &&
      start.getDate() === end.getDate()
    ) {
      return formatRoundDate(startValue);
    }
    if (start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth()) {
      const dayStart = start.toLocaleDateString(undefined, { day: 'numeric' });
      const dayEnd = end.toLocaleDateString(undefined, { day: 'numeric' });
      const month = start.toLocaleDateString(undefined, { month: 'short' });
      return `${dayStart} – ${dayEnd} ${month}`;
    }
    return `${formatRoundDate(startValue)} – ${formatRoundDate(endValue)}`;
  };

  const renderHomeLoading = () => (
    <>
      <header className="betting-header">
        <h1 className="betting-title">This week&apos;s matches</h1>
        <p className="betting-subtitle">
          Choose a league, check the current round, and see what&apos;s live now.
        </p>
        <LeagueCirclePlaceholders />
      </header>
      <LoadingScreen />
    </>
  );

  const renderMatchesHeader = (round = null) => (
    <header className="betting-header">
      <h1 className="betting-title">This week&apos;s matches</h1>
      <p className="betting-subtitle">
        Choose a league, check the current round, and see what&apos;s live now.
      </p>
      <div className="league-selector">
        <LeagueCircleSelector
          leagues={preferredLeagues}
          selectedLeague={selectedLeague}
          onSelect={handleLeagueSelect}
        />
        {selectedLeague && (
          <span className="league-name">{selectedLeague.name}</span>
        )}
        {round != null && (
          <span className="league-name">
            {round.roundStartDate && round.roundEndDate
              ? `Round ${formatRoundDisplay(round.round) || 'N/A'} · ${formatRoundDateRange(round.roundStartDate, round.roundEndDate)}`
              : `Round ${formatRoundDisplay(round.round) || 'N/A'}`}
          </span>
        )}
      </div>
    </header>
  );

  if (!selectedLeague) {
    return (
      <div className="betting-page home-page">
        <div className="betting-content">
          {renderHomeLoading()}
        </div>
      </div>
    );
  }

  return (
    <div className="betting-page home-page">
      {!user && (
        <section className="home-hero-card">
          <p className="home-hero-kicker">Football predictions with an edge</p>
          <h1 className="home-hero-title">Predict every scoreline. Use plays. Climb the table.</h1>
          <p className="home-hero-subtitle">
            Each round you lock in the full card before kickoff, then watch precision and bold calls pay off.
          </p>
          <p className="home-hero-scoring">
            The closer you are to the right score, the more points you get, and the fewer people who nail it, the bigger the payout.
          </p>
          <div className="hero-play-showcase">
            <p className="hero-play-showcase__caption">Use plays to swing matches your way</p>
            <div className="hero-play-showcase__grid" aria-label="Plays and styles preview">
              {HERO_PLAY_SHOWCASE.map((play, index) => (
                <div
                  key={play.key}
                  className="hero-play-tile"
                  style={{ '--hero-tile-i': index }}
                  title={play.shortDesc ? `${play.label}: ${play.shortDesc}` : play.label}
                >
                  <img src={play.svg} alt="" className="hero-play-tile__icon" />
                  <span className="hero-play-tile__label">{play.label}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="home-hero-actions">
            <Link
              to="/register"
              className="action-btn action-btn--primary"
              onClick={() => trackHomeEvent('home_register_click', { source: 'hero' })}
            >
              Join Free
            </Link>
            <Link
              to="/login"
              className="action-btn action-btn--secondary"
              onClick={() => trackHomeEvent('home_login_click', { source: 'hero' })}
            >
              Login
            </Link>
          </div>
        </section>
      )}

      {error && <div className="error-message">{error}</div>}

      {liveResultToast && (
        <div className="home-live-toast" role="status">
          <span>{liveResultToast.message}</span>
          <Link
            to="/standings"
            className="home-results-cta__link"
            onClick={() => setLiveResultToast(null)}
          >
            Standings
          </Link>
          <button
            type="button"
            className="home-live-toast__dismiss"
            aria-label="Dismiss"
            onClick={() => setLiveResultToast(null)}
          >
            ×
          </button>
        </div>
      )}

      {user && resultsCta && resultsCta.leagueCode === selectedLeague?.code && (
        <div className="home-results-cta" role="status">
          <div className="home-results-cta__text">
            <strong>
              {resultsCta.count === 1
                ? '1 result is in'
                : `${resultsCta.count} results are in`}
            </strong>
            <span> - check the standings for {selectedLeague?.name || resultsCta.leagueCode}.</span>
          </div>
          <div className="home-results-cta__actions">
            <Link
              to="/standings"
              className="action-btn action-btn--primary"
              onClick={() => dismissResultsCta(resultsCta.candidates)}
            >
              View standings
            </Link>
            <button
              type="button"
              className="action-btn action-btn--secondary"
              onClick={() => dismissResultsCta(resultsCta.candidates)}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {selectedLeague && !user && (
        <div className="betting-content">
          <section id="how-it-works" className="home-section-card">
            <h2 className="home-section-title">How it works</h2>
            <div className="how-it-works-grid">
              <div className="how-step">
                <span className="how-step-index">1</span>
                <h3>Predict the scores</h3>
                <p>Set a home and away score for every match in the round before it locks.</p>
              </div>
              <div className="how-step">
                <span className="how-step-index">2</span>
                <h3>Add plays for leverage</h3>
                <p>Spend tactical points on plays that boost, split, steal, or block when a match needs an edge.</p>
              </div>
              <div className="how-step">
                <span className="how-step-index">3</span>
                <h3>Climb solo or with friends</h3>
                <p>Rise through divisions on the public table, or create a private league and clash head-to-head each week.</p>
              </div>
            </div>
            <p className="how-it-works-more">
              Want the full picture?{' '}
              <Link to="/about">Read how ScoreClash works</Link>
            </p>
          </section>

          <section className="home-section-card">
            <h2 className="home-section-title">Why join now</h2>
            <p className="home-urgency-message">{getUrgencyMessage()}</p>
            {featuredGames.length > 0 && (
              <div className="featured-games">
                {featuredGames.map((game) => (
                  <div key={`${game.gameId}-featured`} className="featured-game-chip">
                    <TeamName name={game.homeTeam} className="featured-game-team" />
                    <span className="featured-game-separator">vs</span>
                    <TeamName name={game.awayTeam} className="featured-game-team" />
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      {loading && selectedLeague && !currentWeek && (
        <div className="betting-content">
          {renderHomeLoading()}
        </div>
      )}

      {selectedLeague && leagueLoading && loadedLeagueCode !== selectedLeague.code && (
        <div className="betting-content">
          {renderMatchesHeader()}
          <LoadingScreen />
        </div>
      )}

      {selectedLeague && loadedLeagueCode === selectedLeague.code && currentWeek && (
        <div className="betting-content">
          {renderMatchesHeader(currentWeek)}

          {games.length > 0 && (
            <div className="games-grid">
              {sortedGames.map(game => {
                const gameFinished = game.status === 'finished' || game.status === 'postponed' || game.status === 'canceled' || game.status === 'cancelled';
                const gameVoided = isVoidedGame(game);
                const gameLive = isGameLive(game);
                const gameLocked = game.status === 'locked';
                const voteBoosted = game.pickStats?.voteBoosted === true;
                return (
                  <div
                    key={game.gameId}
                    className={`game-card ${gameLocked ? 'game-card--locked' : ''} ${gameLive ? 'game-card--live' : ''} ${gameFinished ? 'game-card--finished' : ''}`}
                  >
                    <div className="game-card-header">
                      <span className="game-date">{new Date(game.gameDate).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                      <div className="game-card-header-right">
                        {voteBoosted && (
                          <span
                            className="match-vote-badge match-vote-badge--community"
                            title="Community Top 3: players who voted for this match get +0.1 on their points multiplier"
                          >
                            <span>Top 3</span>
                          </span>
                        )}
                        {gameFinished ? (
                          <span className="game-status game-status--finished">{getGameStatusLabel(game, games)}</span>
                        ) : gameLive ? (
                          <LiveMatchBadge game={game} />
                        ) : gameLocked ? (
                          <span className="game-status game-status--locked">Locked</span>
                        ) : (
                          <span className="game-status game-status--upcoming">{game.status || 'Upcoming'}</span>
                        )}
                        <MatchLeagueTablePopover
                          leagueCode={selectedLeague.code}
                          homeTeam={game.homeTeam}
                          awayTeam={game.awayTeam}
                          stage={game.stage}
                          games={games}
                        />
                      </div>
                    </div>

                    <div className="game-teams">
                      <div className="game-team game-team--home">
                        <div className="team-head">
                          <ClubCrest leagueCode={selectedLeague.code} teamLabel={game.homeTeam} teamTla={game.homeTeamTla} />
                          <TeamName name={game.homeTeam} />
                        </div>
                        <span className="team-score">{game.homeScore ?? '–'}</span>
                      </div>
                      <span className="game-vs">vs</span>
                      <div className="game-team game-team--away">
                        <div className="team-head">
                          <ClubCrest leagueCode={selectedLeague.code} teamLabel={game.awayTeam} teamTla={game.awayTeamTla} />
                          <TeamName name={game.awayTeam} />
                        </div>
                        <span className="team-score">{game.awayScore ?? '–'}</span>
                      </div>
                    </div>

                    <MatchEventsSection game={game} />

                    {(gameLive || gameFinished) && !gameVoided && (
                      <GameStatsRecap
                        pickStats={game.pickStats}
                        homeScore={game.homeScore}
                        awayScore={game.awayScore}
                        // Home is community-facing: Top 3 matches show the voter stack (same as Picks when voted)
                        playerVotedForMatch={voteBoosted}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="home-actions">
            <Link
              to={user ? "/standings" : "/register"}
              className="action-btn action-btn--primary"
              onClick={() => trackHomeEvent(user ? 'home_standings_click' : 'home_register_click', { source: 'footer_actions' })}
            >
              {user ? 'ScoreClash standings' : 'Create account'}
            </Link>
            <Link
              to={user ? "/picks" : "/standings"}
              className="action-btn action-btn--secondary"
              onClick={() => trackHomeEvent(user ? 'home_betting_click' : 'home_standings_click', { source: 'footer_actions' })}
            >
              {user ? 'Place Your Picks' : 'Explore standings'}
            </Link>
          </div>
        </div>
      )}

      {!loading && !leagueLoading && selectedLeague && loadedLeagueCode === selectedLeague.code && !currentWeek && (
        <div className="empty-state">
          {renderMatchesHeader()}
          <p>No current round available for {selectedLeague.name}. Check back later!</p>
        </div>
      )}
    </div>
  );
};

export default Home;
